import { useCallback, useEffect, useState } from 'react';
import { fetchPackageById } from '../api/Packages';
import { CustomActivityInclusion, CustomFlight, InsuranceDetail, TransferPricing, VisaDetail } from '../types/PackageBuilder';
import { Client, ItineraryItem, TripInfo } from '../types/Quotation';
import { PackageCabPricingRow, TravelPackage } from '../types/TravelPackage';
import { BreakdownCategoryTotals, emptyBreakdownCategoryTotals } from '../components/PackageBuilder/contracts';
import { normalizePackageCabPricing, normalizePackagePlaces, normalizePackageRouteLegs, normalizeStringArray } from '../utils/packageTemplate';
import { getDatePlusDays } from '../utils/date';

// The package search endpoint already returns the route shown to the user.
// Keep its explicit stops when the detail endpoint still exposes older duration data.
export const preferSelectedPackageStops = (details: any, selected: any) => {
  const selectedStops = Array.isArray(selected?.stops) && selected.stops.length > 0
    ? selected.stops
    : Array.isArray(selected?.places) && selected.places.length > 0
      ? selected.places
      : null;

  return selectedStops ? { ...details, stops: selectedStops } : details;
};

type UsePackagePrefillArgs = {
  prefillStartDateFromPackage?: boolean;
  packageFromDraft?: unknown;
  packageToEdit?: TravelPackage;
  packageToCopy?: TravelPackage;
  nightsCount: number;
  tripInfoStartDate: string;
  setItinerary: React.Dispatch<React.SetStateAction<ItineraryItem[]>>;
  setTripInfo: React.Dispatch<React.SetStateAction<TripInfo>>;
  setPriceOverrides: React.Dispatch<React.SetStateAction<{
    flights: number | null;
    hotels: number | null;
    transfers: number | null;
    visa: number | null;
    insurance: number | null;
    activities: number | null;
    addons: number | null;
  }>>;
  setPricePer: React.Dispatch<React.SetStateAction<'person' | 'package' | 'night'>>;
  setCurrency: React.Dispatch<React.SetStateAction<'INR' | 'USD'>>;
  setMarkupType: React.Dispatch<React.SetStateAction<'percent' | 'fixed'>>;
  setMarkupValue: React.Dispatch<React.SetStateAction<number>>;
  setRating: React.Dispatch<React.SetStateAction<string>>;
  setHotelCategory: React.Dispatch<React.SetStateAction<string>>;
  setVisaDetails: React.Dispatch<React.SetStateAction<VisaDetail[]>>;
  setInsuranceDetails: React.Dispatch<React.SetStateAction<InsuranceDetail[]>>;
  setCustomVisaDetails: React.Dispatch<React.SetStateAction<VisaDetail[]>>;
  setCustomInsuranceDetails: React.Dispatch<React.SetStateAction<InsuranceDetail[]>>;
  setCustomFlights: React.Dispatch<React.SetStateAction<CustomFlight[]>>;
  setCustomActivityInclusions: React.Dispatch<React.SetStateAction<CustomActivityInclusion[]>>;
  setStrikePrice: React.Dispatch<React.SetStateAction<number>>;
  setPricePerPerson: React.Dispatch<React.SetStateAction<number>>;
  setOfferPrice: React.Dispatch<React.SetStateAction<number>>;
  setQuotationBasePrice: React.Dispatch<React.SetStateAction<number>>;
  setHighlights: React.Dispatch<React.SetStateAction<string[]>>;
  setMealPlan: React.Dispatch<React.SetStateAction<string>>;
  setTransferType: React.Dispatch<React.SetStateAction<string>>;
  setTransferPricing: React.Dispatch<React.SetStateAction<TransferPricing>>;
  setQuotationPackageId: React.Dispatch<React.SetStateAction<string | null>>;
  setQuotationTransferRows: React.Dispatch<React.SetStateAction<PackageCabPricingRow[]>>;
  setQuotationTransferSelection?: React.Dispatch<React.SetStateAction<{
    sourceRowId?: string;
    cabType?: string;
  } | null>>;
  setIdealFor: React.Dispatch<React.SetStateAction<string[]>>;
  setIdealVendor: React.Dispatch<React.SetStateAction<string>>;
  setSearchTags: React.Dispatch<React.SetStateAction<string[]>>;
  setRelatedDestinations: React.Dispatch<React.SetStateAction<string[]>>;
  setRelatedPackages: React.Dispatch<React.SetStateAction<string[]>>;
  setExclusions: React.Dispatch<React.SetStateAction<string[]>>;
  setAddons: React.Dispatch<React.SetStateAction<Array<{ name: string; price: number; currency: string; description: string; quoteBasePrice?: number }>>>;
  setReviews: React.Dispatch<React.SetStateAction<Array<{ date: string; user: string; rating: number; comment: string }>>>;
  setFaq: React.Dispatch<React.SetStateAction<Array<{ question: string; answer: string }>>>;
  setIsPublished: React.Dispatch<React.SetStateAction<boolean>>;
  setEditingId: React.Dispatch<React.SetStateAction<string | null>>;
  setQuotationCreatedAt: React.Dispatch<React.SetStateAction<string | undefined>>;
  setClient: React.Dispatch<React.SetStateAction<Client>>;
  setQuotationSource: React.Dispatch<React.SetStateAction<string>>;
  setPersistedBreakdown: React.Dispatch<React.SetStateAction<BreakdownCategoryTotals | null>>;
  setInitialLiveTotals: React.Dispatch<React.SetStateAction<BreakdownCategoryTotals | null>>;
};

export const usePackagePrefill = ({
  prefillStartDateFromPackage = true,
  packageFromDraft,
  packageToEdit,
  packageToCopy,
  nightsCount,
  tripInfoStartDate,
  setItinerary,
  setTripInfo,
  setPriceOverrides,
  setPricePer,
  setCurrency,
  setMarkupType,
  setMarkupValue,
  setRating,
  setHotelCategory,
  setVisaDetails,
  setInsuranceDetails,
  setCustomVisaDetails,
  setCustomInsuranceDetails,
  setCustomFlights,
  setCustomActivityInclusions,
  setStrikePrice,
  setPricePerPerson,
  setOfferPrice,
  setQuotationBasePrice,
  setHighlights,
  setMealPlan,
  setTransferType,
  setTransferPricing,
  setQuotationPackageId,
  setQuotationTransferRows,
  setQuotationTransferSelection,
  setIdealFor,
  setIdealVendor,
  setSearchTags,
  setRelatedDestinations,
  setRelatedPackages,
  setExclusions,
  setAddons,
  setReviews,
  setFaq,
  setIsPublished,
  setEditingId,
  setQuotationCreatedAt,
  setClient,
  setQuotationSource,
  setPersistedBreakdown,
  setInitialLiveTotals,
}: UsePackagePrefillArgs) => {
  const [prefilled, setPrefilled] = useState(false);
  const [pendingItinerary, setPendingItinerary] = useState<ItineraryItem[] | null>(null);
  const [pendingApply, setPendingApply] = useState(false);
  const quotationMode = !prefillStartDateFromPackage;

  function coerceStringArray(arr: any): string[] {
    if (!Array.isArray(arr)) return [];
    const out: string[] = [];
    arr.forEach((v) => {
      if (typeof v === 'string') {
        const s = v.trim();
        const inner = s.replace(/[{}]/g, '').replace(/"/g, '"');
        if (inner.includes(',')) {
          inner.split(',').forEach(p => { const t = p.replace(/"/g, '"').replace(/"/g, '').trim(); if (t) out.push(t); });
        } else if (inner) {
          const t = inner.replace(/"/g, '').trim(); if (t) out.push(t);
        }
      }
    });
    return out.filter(Boolean);
  }

  function coerceCities(cities: any): string[] {
    if (Array.isArray(cities) && cities.length > 0) {
      const raw = cities.map((c: any) => {
        if (typeof c === 'string') return c;
        if (c && typeof c === 'object') {
          return c.name || c.city || c.label || c.value || '';
        }
        return String(c);
      });
      const vals = raw.map(s => s.replace(/[{}]/g, '').replace(/"/g, '').replace(/"/g, '').trim()).filter(Boolean);
      return vals.length ? vals : raw;
    }
    return [];
  }

  function coerceHighlights(value: any): string[] {
    if (Array.isArray(value)) {
      return normalizeStringArray(value);
    }
    if (typeof value === 'string') {
      return value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
    }
    return [];
  }

  const readPackageId = (pkg: any) => (
    String(
      pkg?.packageId
      || pkg?.templateId
      || pkg?.template_id
      || pkg?.quotation?.templateId
      || pkg?.quotation?.template_id
      || pkg?.id
      || ''
    ).trim()
  );

  const getCabPricingRows = (pkg: any) => (
    Array.isArray(pkg?.packageCabPricing)
      ? pkg.packageCabPricing
      : Array.isArray(pkg?.package_cab_pricing)
        ? pkg.package_cab_pricing
        : Array.isArray(pkg?.cab_pricing)
          ? pkg.cab_pricing
          : []
  );

  const readTransferSelection = (pkg: any) => {
    const transferCalculation = pkg?.transferCalculation || pkg?.transfer_calculation;
    if (!transferCalculation || typeof transferCalculation !== 'object') return null;

    const sourceRowId = String(
      transferCalculation.sourceRowId
      || transferCalculation.source_row_id
      || ''
    ).trim();
    const cabType = String(
      transferCalculation.cabType
      || transferCalculation.cab_type
      || ''
    ).trim();

    if (!sourceRowId && !cabType) return null;
    return {
      ...(sourceRowId ? { sourceRowId } : {}),
      ...(cabType ? { cabType } : {}),
    };
  };

  const prefillFromApi = useCallback(async (p: any) => {
    const normalizedPlaces = normalizePackagePlaces(p);
    const durationNights = Number(
      p.duration_nights
      || p.durationNights
      || normalizedPlaces.reduce((sum, place) => sum + place.nights, 0)
      || 0
    ) || 0;
    const apiCities = normalizedPlaces.length > 0
      ? normalizedPlaces.map((place) => place.cityName)
      : coerceCities(p.cities || []);
    const primaryCity = apiCities[0] || p.title || '';

    let places: any[] = [];
    if (normalizedPlaces.length > 0) {
      places = normalizedPlaces.map((place) => ({
        label: place.cityName,
        value: place.cityCode || place.cityName,
        nights: place.nights,
      }));
    } else if (apiCities.length > 0) {
      const baseNights = Math.floor(durationNights / apiCities.length);
      const extra = durationNights % apiCities.length;
      places = apiCities.map((c, idx) => ({ label: c, value: c, nights: baseNights + (idx < extra ? 1 : 0) }));
    } else {
      places = [{ label: primaryCity, value: primaryCity, nights: durationNights }];
    }

    const priceInfo = p.price || {};
    const per = priceInfo.per;
    let unit: 'person' | 'package' | 'night' = 'person';
    if (per === 'person' || per === 'per_person') unit = 'person';
    else if (per === 'total' || per === 'per_couple' || per === 'package') unit = 'package';
    else if (per === 'night') unit = 'night';

    setTripInfo(prev => ({
      ...prev,
      title: p.title || '',
      subtitle: p.subtitle || '',
      tripOverview: p.description || p.tripOverview || p.trip_overview || '',
      places,
      routeLegs: normalizePackageRouteLegs(p.routeLegs ?? p.route_legs),
      startDate: prefillStartDateFromPackage ? ((p.itinerary && p.itinerary[0]?.date) || prev.startDate) : prev.startDate,
      inclusions: coerceStringArray(p.inclusions),
      totalPrice: Number(priceInfo.base || 0) || 0,
    }));
    setHighlights(Array.from(new Set(
      coerceHighlights(p.highlights)
        .concat(coerceHighlights(p.packageConfig?.highlights))
        .concat(coerceHighlights(p.package_config?.highlights))
    )));
    const sourceRaw = p.source || p.quotation?.source;
    if (sourceRaw) {
      setQuotationSource(String(sourceRaw));
    }
    const createdAtRaw = (p as any).created_at || (p as any).createdAt || (p as any).quotation?.created_at || (p as any).quotation?.createdAt;
    if (createdAtRaw) {
      setQuotationCreatedAt(String(createdAtRaw));
    }
    const clientRaw = p.client || p.quotation?.client;
    if (clientRaw) {
      let parsedClient = clientRaw;
      if (typeof parsedClient === 'string') {
        try {
          parsedClient = JSON.parse(parsedClient);
        } catch {
          parsedClient = null;
        }
      }
      if (parsedClient && typeof parsedClient === 'object') {
        const next = parsedClient as Partial<Client>;
        setClient(prev => ({
          ...prev,
          name: next.name ?? prev.name,
          email: next.email ?? prev.email,
          phoneNumber: (next as any).phoneNumber ?? prev.phoneNumber,
        }));
      }
    }
    setPricePer(unit);
    setCurrency((priceInfo.currency === 'USD' || priceInfo.currency === 'INR') ? priceInfo.currency : 'INR');
    const basePrice = Number(priceInfo.base || 0) || 0;
    const strike = Number(priceInfo.strike || 0) || 0;
    const storedMarkup = (priceInfo as any)?.breakdown?.markup
      || (p as any)?.price_breakdown?.markup
      || (priceInfo as any)?.markup
      || (p as any)?.pricing?.markup;
    if (storedMarkup && (storedMarkup.type === 'percent' || storedMarkup.type === 'fixed')) {
      setMarkupType(storedMarkup.type);
      const rawValue = storedMarkup.value ?? storedMarkup.percent ?? 0;
      setMarkupValue(Number(rawValue || 0));
    }
    const breakdown = (priceInfo as any)?.breakdown
      || (priceInfo as any)?.price_breakdown
      || (p as any)?.price_breakdown
      || (p as any)?.pricing?.breakdown;
    if (breakdown && typeof breakdown === 'object') {
      const toBreakdownValue = (value: any) => {
        const num = Number(value);
        return Number.isFinite(num) ? num : 0;
      };
      setPersistedBreakdown({
        flights: toBreakdownValue(breakdown.flights ?? breakdown.flight),
        hotels: toBreakdownValue(breakdown.hotels ?? breakdown.hotel),
        transfers: toBreakdownValue(breakdown.transfers ?? breakdown.transfer),
        visa: toBreakdownValue(breakdown.visa),
        insurance: toBreakdownValue(breakdown.insurance),
        activities: toBreakdownValue(breakdown.activities ?? breakdown.activity),
        addons: toBreakdownValue(breakdown.addons ?? breakdown.addon),
      });
      setInitialLiveTotals(null);
    } else {
      setPersistedBreakdown(null);
      setInitialLiveTotals(null);
    }
    setPriceOverrides({
      ...emptyBreakdownCategoryTotals,
      flights: null,
      hotels: null,
      transfers: null,
      visa: null,
      insurance: null,
      activities: null,
      addons: null,
    });
    const categoryValue = p.hotel_category || p.hotelCategory || p.rating || '';
    setRating(categoryValue || '');
    setHotelCategory(categoryValue || '');
    const addonsSum = Array.isArray(p.addons) ? p.addons.reduce((sum: number, addon: any) => sum + (Number(addon?.price || 0) || 0), 0) : 0;
    const baseForQuotation = addonsSum > 0 && basePrice >= addonsSum ? basePrice - addonsSum : basePrice;
    const visaRaw = (p as any).visaDetails || (p as any).visa_details;
    if (Array.isArray(visaRaw)) {
      setVisaDetails(visaRaw);
    } else if (typeof visaRaw === 'string') {
      try {
        const parsed = JSON.parse(visaRaw);
        if (Array.isArray(parsed)) setVisaDetails(parsed);
      } catch {
        // ignore
      }
    }
    const insuranceRaw = (p as any).insuranceDetails || (p as any).insurance_details;
    if (Array.isArray(insuranceRaw)) {
      setInsuranceDetails(insuranceRaw);
    } else if (typeof insuranceRaw === 'string') {
      try {
        const parsed = JSON.parse(insuranceRaw);
        if (Array.isArray(parsed)) setInsuranceDetails(parsed);
      } catch {
        // ignore
      }
    }
    const customisableRaw = (p as any).customisableInclusions || (p as any).customisable_inclusions;
    if (customisableRaw) {
      try {
        const parsed = typeof customisableRaw === 'string' ? JSON.parse(customisableRaw) : customisableRaw;
        if (Array.isArray(parsed?.visaDetails)) setCustomVisaDetails(parsed.visaDetails);
        if (Array.isArray(parsed?.insuranceDetails)) setCustomInsuranceDetails(parsed.insuranceDetails);
        if (Array.isArray(parsed?.flights)) setCustomFlights(parsed.flights);
        if (Array.isArray(parsed?.activities)) setCustomActivityInclusions(parsed.activities);
        if (parsed?.transferPricing || parsed?.transfer_prices) {
          const raw = parsed?.transferPricing || parsed?.transfer_prices || {};
          setTransferPricing({
            shared: Number(raw.shared ?? raw.shared_price ?? raw.sharedPrice ?? 0) || null,
            private: Number(raw.private ?? raw.private_price ?? raw.privatePrice ?? 0) || null,
          });
        }
      } catch {
        // ignore
      }
    }
    setStrikePrice(strike);
    setPricePerPerson(strike || basePrice);
    setOfferPrice(basePrice);
    setQuotationBasePrice(baseForQuotation);
    setHighlights(normalizeStringArray(p.highlights));
    setMealPlan(p.meal_plan || p.mealPlan || 'None');
    setTransferType(p.transfer_type || p.transferType || '');
    setIdealFor(coerceStringArray(p.ideal_for || p.idealFor));
    setIdealVendor(p.ideal_vendor || p.idealVendor || '');
    setSearchTags(normalizeStringArray(p.search_tags || p.searchTags));
    setRelatedDestinations(coerceStringArray(p.related_destinations || p.relatedDestinations));
    setRelatedPackages(coerceStringArray(p.related_packages || p.relatedPackages));
    setExclusions(coerceStringArray(p.exclusions));
    setAddons(Array.isArray(p.addons) ? p.addons : []);
    setReviews(Array.isArray(p.reviews) ? p.reviews : []);
    setFaq(Array.isArray(p.faq) ? p.faq : []);
    setIsPublished(!!(p.is_published || p.isPublished));
    if (packageToEdit && p.id) setEditingId(String(p.id));

    const draftTransferSelection = readTransferSelection(p);
    if (setQuotationTransferSelection) {
      setQuotationTransferSelection(draftTransferSelection);
    }

    let cabSource = p;
    const resolvedPackageId = readPackageId(p);
    const cabPricingRows = getCabPricingRows(p);
    if (quotationMode && cabPricingRows.length === 0 && resolvedPackageId) {
      try {
        const fetched = await fetchPackageById(resolvedPackageId);
        cabSource = { ...p, ...fetched };
      } catch {
        cabSource = p;
      }
    }

    setQuotationTransferRows(normalizePackageCabPricing(cabSource));
    setQuotationPackageId(resolvedPackageId || (p?.id ? String(p.id) : null));

    const packageTransferRows = normalizePackageCabPricing(cabSource);
    const hotelsFromApi = Array.isArray((p as any).hotels) ? (p as any).hotels : [];
    const useDetailedHotels = hotelsFromApi.some((h: any) =>
      h?.placeIndex !== undefined
      || h?.place_index !== undefined
      || h?.checkInDate
      || h?.check_in_date
      || h?.roomVariantId
      || h?.room_variant_id
    );
    const targetLen = Math.max(1, durationNights + 1);
    const cityLabel = primaryCity;
    const byDay = (arr: any[], key = 'day') => {
      const m = new Map<number, any[]>();
      (Array.isArray(arr) ? arr : []).forEach(it => { const d = Number(it[key] || 0); if (!m.has(d)) m.set(d, []); m.get(d)!.push(it); });
      return m;
    };
    const itinDays = Array.isArray(p.itinerary) ? p.itinerary : [];
    const topLevelFlights = Array.isArray(p.flights) ? p.flights : [];
    const topLevelActivities = Array.isArray(p.activities) ? p.activities : [];
    const topLevelTransfers = Array.isArray(p.transfers) ? p.transfers : [];
    const itineraryFlights = itinDays.flatMap((d: any) =>
      Array.isArray(d.flights) ? d.flights.map((flight: any) => ({ ...flight, day: d.day })) : []
    );
    const itineraryActivities = itinDays.flatMap((d: any) =>
      Array.isArray(d.activities) ? d.activities.map((activity: any) => ({ ...activity, day: d.day })) : []
    );
    const itineraryTransfers = itinDays.flatMap((d: any) =>
      Array.isArray(d.transfers) ? d.transfers.map((transfer: any) => ({ ...transfer, day: d.day })) : []
    );
    const flightsByDay = byDay(topLevelFlights.length > 0 ? topLevelFlights : itineraryFlights, 'day');
    // The package response may provide selected activities at the top level without
    // a day. Keep every one of those activities in the quotation by assigning
    // undated entries to the first itinerary day. The API does not currently
    // provide a reliable itinerary-day mapping for that response shape.
    const activitySource = topLevelActivities.length > 0 ? topLevelActivities : itineraryActivities;
    const activitiesByDay = byDay(
      activitySource.map((activity: any) => ({
        ...activity,
        day: Number(activity.day || 0) > 0 ? Number(activity.day) : 1,
      })),
      'day'
    );
    const transfersByDay = byDay(topLevelTransfers.length > 0 ? topLevelTransfers : itineraryTransfers, 'day');
    const hotelsByDay = new Map<number, any[]>();
    if (!useDetailedHotels) {
      if (itinDays.length) {
        itinDays.forEach((d: any) => { if (Array.isArray(d.hotels)) hotelsByDay.set(Number(d.day || 0), d.hotels); });
      } else if (Array.isArray(p.hotels) && p.hotels.length) {
        hotelsByDay.set(1, p.hotels);
      }
    }

    const buildFlight = (f: any) => ({
      provider: f.provider || 'API',
      airline: f.airline || '',
      flightNumber: f.flightNumber || f.number || '',
      from: f.from || '',
      to: f.to || '',
      departureTime: f.departureTime || f.boardingDateTime || (f.date ? `${f.date}T00:00:00` : ''),
      arrivalTime: f.arrivalTime || f.landingDateTime || (f.date ? `${f.date}T00:00:00` : ''),
      duration: f.duration || '',
      stops: Number(f.stops || 0),
      price: Number(f.price || 0),
      class: f.class || '',
    });
    const buildHotel = (h: any) => ({
      provider: h.provider || h.source || h.supplier || '',
      hotelCode: h.hotelCode || h.hotel_code || h.hotelId || h.hotel_id || h.providerHotelId || h.provider_hotel_id || h.hotelRef || h.hotel_ref || '',
      hotelRef: h.hotelRef || h.hotel_ref || h.ref || h.providerHotelId || h.provider_hotel_id || h.hotelId || h.hotel_id || h.id || '',
      hotelId: h.hotelId || h.hotel_id || h.id || h.hotelRef || h.hotel_ref || h.ref || '',
      name: h.name || '',
      starRating: String(h.rating || h.starRating || h.star_rating || '0'),
      address: h.address || '',
      description: h.description || '',
      city_name: h.city_name || h.cityName || h.city || '',
      city_code: h.city_code || h.cityCode || '',
      checkin_time: (h as any).checkin_time || '',
      checkout_time: (h as any).checkout_time || '',
      latitude: Number(h.latitude || 0),
      longitude: Number(h.longitude || 0),
      phone_number: h.phone_number || h.phone || '',
      price: Number(h.price || h.display_price || 0),
      currency: h.currency || h.display_currency || 'INR',
      display_price: Number(h.display_price || h.price || 0),
      display_currency: h.display_currency || h.currency || 'INR',
      amenities: Array.isArray(h.amenities) ? h.amenities : [],
      images: Array.isArray(h.images) ? h.images : [],
      gallery: Array.isArray(h.gallery) ? h.gallery : [],
      roomType: h.roomType || h.room_type || h.selected_room?.name || h.selectedRoom?.name || '',
      roomVariantId: h.roomVariantId || h.room_variant_id || h.selected_room?.id || h.selectedRoom?.id || '',
      thumbnail: h.thumbnail
        || h.image
        || h.imageUrl
        || h.image_url
        || h.thumbnailUrl
        || h.thumbnail_url
        || h.picture_url
        || (Array.isArray(h.images) ? h.images[0] : '')
        || (Array.isArray(h.gallery) ? h.gallery[0] : '')
        || '',
    });

    const buildActivity = (a: any) => {
      const paisePrice = Number(a.from_price_paise ?? a.fromPricePaise);
      const rupeePrice = Number(a.price);

      return ({
      id: a.id,
      tour_id: Number((a.tour_id ?? a.tourId ?? a.id) || 0) || undefined,
      contract_id: Number((a.contract_id ?? a.contractId) || 0) || undefined,
      city: a.city,
      city_id: Number((a.city_id ?? a.cityId) || 0) || undefined,
      name: a.name,
      slug: a.slug || (a.id ? `tour-${a.id}` : ''),
      description: a.description || a.activity_description || '',
      duration_minutes: a.duration_minutes,
      start_time: a.start_time,
      remarks: a.remarks,
      rules: a.rules,
      picture_url: a.picture_url || a.pictureUrl || a.image_url || '',
      category: a.category,
      currency: a.currency || 'INR',
      is_active: typeof a.is_active === 'boolean' ? a.is_active : true,
      // Package activities expose `price` in rupees. The activity picker uses
      // paise, so normalize both sources to the same field for pricing and save.
      from_price_paise: Number.isFinite(paisePrice)
        ? Math.round(paisePrice)
        : Number.isFinite(rupeePrice)
          ? Math.round(rupeePrice * 100)
          : undefined,
      adult_price_paise: Number.isFinite(Number(a.adult_price_paise))
        ? Math.round(Number(a.adult_price_paise))
        : undefined,
      child_price_paise: Number.isFinite(Number(a.child_price_paise))
        ? Math.round(Number(a.child_price_paise))
        : undefined,
      variant_count: a.variant_count,
      variants: a.variants,
    });
    };

    const resolvePlaceStartDay = (placeIdx: number) => {
      if (placeIdx < 0) return 1;
      let dayNo = 1;
      for (let i = 0; i < placeIdx; i += 1) {
        dayNo += Number(places[i]?.nights || 1);
      }
      return dayNo;
    };

    const resolvePlaceIndexFromLabel = (label?: string) => {
      const normalizedLabel = String(label || '').trim().toLowerCase();
      if (!normalizedLabel) return -1;
      return places.findIndex((place) => {
        const candidates = [
          place?.label,
          place?.value,
        ]
          .map((candidate) => String(candidate || '').trim().toLowerCase())
          .filter(Boolean);
        return candidates.includes(normalizedLabel);
      });
    };

    const resolvePlaceIndexForItineraryDay = (dayIndex: number, fallbackLabel?: string) => {
      const fromLabel = resolvePlaceIndexFromLabel(fallbackLabel);
      if (fromLabel >= 0) return fromLabel;

      let cursor = 0;
      for (let idx = 0; idx < places.length; idx += 1) {
        const nights = Math.max(1, Number(places[idx]?.nights || 1));
        const start = cursor;
        const end = start + nights - 1;
        if (dayIndex >= start && dayIndex <= end) return idx;
        cursor += nights;
      }

      return Math.max(0, places.length - 1);
    };

    const deriveQuotationHotelStayDates = (placeIdx: number) => {
      if (!tripInfoStartDate) {
        return {
          checkInDate: undefined,
          checkOutDate: undefined,
        };
      }
      const placeStartDay = resolvePlaceStartDay(placeIdx);
      const checkInDate = getDatePlusDays(tripInfoStartDate, Math.max(0, placeStartDay - 1));
      const stayNights = Math.max(1, Number(places[placeIdx]?.nights || 1));
      return {
        checkInDate,
        checkOutDate: getDatePlusDays(checkInDate, stayNights),
      };
    };

    const newItin: ItineraryItem[] = Array.from({ length: targetLen }).map((_, i) => {
      const dayNo = i + 1;
      const dIt = itinDays.find((d: any) => Number(d.day || 0) === dayNo) || {};
      const flights = (flightsByDay.get(dayNo) || []).map(buildFlight);
      const activities = (activitiesByDay.get(dayNo) || []).map(buildActivity);
      const transfers = packageTransferRows.length > 0 ? [] : (transfersByDay.get(dayNo) || []).map((t: any) => ({
        provider: t.provider,
        name: t.name,
        vehicle: t.vehicle,
        price: Number(t.price || 0),
        currency: t.currency || 'INR',
        pickupDate: t.pickupDate || t.date,
        pickupTime: t.pickupTime || t.time,
        pickupFromAirport: t.pickupFromAirport,
        cityId: t.cityId,
        airportCode: t.airportCode,
        hotelCode: t.hotelCode,
        meta: t.meta,
      }));
      const hotels = (hotelsByDay.get(dayNo) || []).map((h: any) => {
        const base = buildHotel(h);
        if (quotationMode) {
          const placeIdx = resolvePlaceIndexForItineraryDay(i, dIt.place?.label || dIt.city);
          const { checkInDate, checkOutDate } = deriveQuotationHotelStayDates(placeIdx);
          return { ...base, checkInDate, checkOutDate } as any;
        }
        const checkInDate = dIt.date || (p.itinerary && p.itinerary[0]?.date) || tripInfoStartDate || undefined;
        const nextDate = (p.itinerary && p.itinerary[i + 1]?.date) || undefined;
        return { ...base, checkInDate, checkOutDate: nextDate } as any;
      });
      const placeLabel = dIt.place?.label || dIt.city || cityLabel;
      const points = Array.isArray(dIt.points) ? dIt.points : [];
      return {
        day: dayNo,
        title: dIt.title || '',
        image: typeof dIt.image === 'string' ? dIt.image : undefined,
        place: { label: placeLabel, value: placeLabel, nights: 1 },
        flights,
        activities,
        hotels,
        transfers,
        points,
      } as any;
    });
    if (hotelsFromApi.length > 0 && useDetailedHotels) {
      const toUtcDate = (value?: string) => {
        if (!value) return null;
        const datePart = String(value).split('T')[0];
        const [y, m, d] = datePart.split('-').map(Number);
        if (!y || !m || !d) return null;
        return Date.UTC(y, m - 1, d);
      };
      const baseStartDate = (p.itinerary && p.itinerary[0]?.date) || tripInfoStartDate || '';
      const baseStartUtc = toUtcDate(baseStartDate);
      const resolvePlaceIndex = (hotel: any): number => {
        const idxRaw = hotel?.placeIndex ?? hotel?.place_index;
        const idx = Number(idxRaw);
        if (Number.isFinite(idx) && idx >= 0) return idx;
        const normalizePlaceLabel = (value: unknown) => String(value || '')
          .trim()
          .replace(/\s+/g, ' ')
          .toLowerCase();
        const city = normalizePlaceLabel(hotel?.city || hotel?.place || hotel?.placeLabel);
        if (!city) return -1;
        return places.findIndex((p) => normalizePlaceLabel(p.label || p.value) === city);
      };
      const resolveHotelDayIndex = (hotel: any, fallbackPlaceIdx: number) => {
        const checkInDate = hotel?.checkInDate || hotel?.check_in_date;
        const checkInUtc = toUtcDate(checkInDate);
        if (baseStartUtc !== null && checkInUtc !== null) {
          const diffDays = Math.floor((checkInUtc - baseStartUtc) / (24 * 60 * 60 * 1000));
          const dayNo = diffDays + 1;
          if (dayNo >= 1 && dayNo <= newItin.length) return dayNo - 1;
        }
        const placeDayNo = resolvePlaceStartDay(fallbackPlaceIdx);
        if (placeDayNo >= 1 && placeDayNo <= newItin.length) return placeDayNo - 1;
        return fallbackPlaceIdx;
      };
      hotelsFromApi.forEach((h: any) => {
        const placeIdx = resolvePlaceIndex(h);
        if (placeIdx < 0) return;
        const dayIndex = quotationMode
          ? resolvePlaceStartDay(placeIdx) - 1
          : resolveHotelDayIndex(h, placeIdx);
        if (dayIndex < 0 || dayIndex >= newItin.length) return;
        const base = buildHotel(h);
        const quotationStayDates = quotationMode ? deriveQuotationHotelStayDates(placeIdx) : null;
        const checkInDate = quotationStayDates?.checkInDate || h.checkInDate || h.check_in_date || undefined;
        const checkOutDate = quotationStayDates?.checkOutDate || h.checkOutDate || h.check_out_date || undefined;
        const prevHotels = newItin[dayIndex]?.hotels || [];
        newItin[dayIndex] = {
          ...newItin[dayIndex],
          hotels: [...prevHotels, { ...base, ...h, checkInDate, checkOutDate } as any],
        };
      });
    }
    setPendingItinerary(newItin);
    setPendingApply(true);
  }, [packageToEdit, prefillStartDateFromPackage, quotationMode, setAddons, setClient, setCustomActivityInclusions, setCustomFlights, setCustomInsuranceDetails, setCustomVisaDetails, setCurrency, setExclusions, setFaq, setHighlights, setHotelCategory, setIdealFor, setIdealVendor, setInitialLiveTotals, setInsuranceDetails, setIsPublished, setMarkupType, setMarkupValue, setMealPlan, setOfferPrice, setPersistedBreakdown, setPriceOverrides, setPricePer, setPricePerPerson, setQuotationBasePrice, setQuotationCreatedAt, setQuotationPackageId, setQuotationSource, setQuotationTransferRows, setQuotationTransferSelection, setRating, setRelatedDestinations, setRelatedPackages, setReviews, setSearchTags, setStrikePrice, setTransferPricing, setTransferType, setTripInfo, setVisaDetails, setEditingId, tripInfoStartDate]);

  useEffect(() => {
    if (!pendingApply || !pendingItinerary) return;
    const expected = pendingItinerary.length - 1;
    if (expected === nightsCount) {
      setItinerary(pendingItinerary);
      setPendingApply(false);
      setPendingItinerary(null);
    }
  }, [pendingApply, pendingItinerary, nightsCount, setItinerary]);

  useEffect(() => {
    if (prefilled) return;
    const p = packageFromDraft || packageToEdit || packageToCopy;
    if (!p) return;
    (async () => {
      try {
        if (packageFromDraft) {
          await prefillFromApi(packageFromDraft);
        } else {
          const id = (p as any).id ? String((p as any).id) : undefined;
          const full = id ? await fetchPackageById(id) : p;
          await prefillFromApi(preferSelectedPackageStops(full, p));
        }
      } finally {
        setPrefilled(true);
      }
    })();
  }, [packageFromDraft, packageToCopy, packageToEdit, prefilled, prefillFromApi]);

  return {
    pendingApply,
    prefilled,
  };
};
